/**
 * KYB contract gate on the moderator deal page (docs/decisions/kyb.md).
 *
 * Shows both companies' verdicts (from their badge panels) with links to
 * their KYB workspaces, the warnings from the latest contract save, and —
 * for super moderators — the per-contract override (POST/DELETE
 * `deals/<id>/contract/kybOverride`). The gate mode (`KYB_CONTRACT_GATE`:
 * off / warn / enforce) is a backend setting.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import KybGateNotice from "@/components/KybStatus/KybGateNotice";
import { type KybWarning, readKybWarnings } from "@/config/api";
import { formatDateTime } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import { VERDICT_CHIP } from "@/lib/kyb";
import type { CompanyBadgePanel, KybOverrideResponse } from "@/services/moderators/generated/model";
import {
  useModeratorsDealsContractKybOverrideCreate,
  useModeratorsDealsContractKybOverrideDestroy,
} from "@/services/moderators/generated/moderators-deals/moderators-deals";
import { useVerificationModeratorCompaniesBadgesRetrieve } from "@/services/moderators/generated/moderators-verification/moderators-verification";
import "@/components/KybStatus/KybFlow.css";
import "./Kyb/Kyb.css";

interface Party {
  side: "buyer" | "supplier";
  id: number | undefined;
  name: string | undefined;
}

const DealKybGateCard = ({
  dealId,
  buyer,
  supplier,
  hasContract,
  isSuper,
  lastWarnings,
}: {
  dealId: number;
  buyer: Omit<Party, "side">;
  supplier: Omit<Party, "side">;
  hasContract: boolean;
  isSuper: boolean;
  /** `kybWarnings` from the latest contract save in this session. */
  lastWarnings: KybWarning[] | null;
}) => {
  const { t } = useTranslation(["kyb", "common"]);
  const [override, setOverride] = useState<KybOverrideResponse | null>(null);

  return (
    <section className="mod-page__card">
      <div className="mod-page__card-body">
        <h2 className="mod-page__card-title">{t("dealGate.title")}</h2>
        <p className="mod-page__card-meta">{t("dealGate.help")}</p>

        <table className="kyb-table" style={{ marginTop: 8 }}>
          <tbody>
            <PartyRow party={{ side: "buyer", ...buyer }} />
            <PartyRow party={{ side: "supplier", ...supplier }} />
          </tbody>
        </table>

        {lastWarnings ? (
          lastWarnings.length > 0 ? (
            <KybGateNotice warnings={lastWarnings} showVerdict />
          ) : (
            <p className="mod-page__card-meta">{t("dealGate.lastSaveClear")}</p>
          )
        ) : null}
        {override ? <KybGateNotice warnings={override.kybWarnings as KybWarning[]} showVerdict /> : null}

        {isSuper && hasContract ? <OverrideForm dealId={dealId} current={override} onChanged={setOverride} /> : null}
        {!hasContract ? <p className="mod-page__card-meta">{t("dealGate.noContract")}</p> : null}
      </div>
    </section>
  );
};

const PartyRow = ({ party }: { party: Party }) => {
  const { t } = useTranslation("kyb");
  const { data } = useVerificationModeratorCompaniesBadgesRetrieve(party.id ?? 0, {
    query: { enabled: Boolean(party.id) },
  });
  const panel = data as CompanyBadgePanel | undefined;
  return (
    <tr>
      <td>{t(`dealGate.side.${party.side}`)}</td>
      <td>{party.id ? <Link to={`/moderator/kyb/companies/${party.id}`}>{party.name}</Link> : party.name}</td>
      <td>
        {panel ? (
          <span className={`mod-page__chip ${VERDICT_CHIP[panel.verdict]}`}>{t(`verdict.${panel.verdict}`)}</span>
        ) : null}
      </td>
      <td>{panel?.publicAward ? panel.publicAward.badgeLabel : t("badges.noPublicBadge")}</td>
    </tr>
  );
};

const OverrideForm = ({
  dealId,
  current,
  onChanged,
}: {
  dealId: number;
  current: KybOverrideResponse | null;
  onChanged: (r: KybOverrideResponse | null) => void;
}) => {
  const { t } = useTranslation(["kyb", "common"]);
  const [reason, setReason] = useState("");
  const create = useModeratorsDealsContractKybOverrideCreate({
    mutation: {
      onSuccess: (r) => {
        setReason("");
        onChanged(r);
      },
    },
  });
  const remove = useModeratorsDealsContractKybOverrideDestroy({ mutation: { onSuccess: (r) => onChanged(r) } });
  const error = create.error ?? remove.error;
  const active = current?.kybOverride ?? null;

  return (
    <div className="kyb-sub">
      <h3 className="kyb-sub__title">{t("dealGate.override.title")}</h3>
      <p className="mod-page__card-meta">{t("dealGate.override.help")}</p>
      {active ? (
        <p className="mod-page__card-meta">
          {t("dealGate.override.active", {
            name: active.byName,
            date: formatDateTime(active.at),
            reason: active.reason,
          })}
        </p>
      ) : null}
      <div className="mod-page__form-row">
        <input
          className="mod-page__input"
          placeholder={t("dealGate.override.reason")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button
          type="button"
          className="mod-page__btn mod-page__btn--primary"
          disabled={!reason.trim() || create.isPending}
          onClick={() => create.mutate({ id: dealId, data: { reason: reason.trim() } })}
        >
          {t("dealGate.override.record")}
        </button>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          disabled={remove.isPending}
          onClick={() => remove.mutate({ id: dealId })}
        >
          {t("dealGate.override.clear")}
        </button>
      </div>
      {error ? (
        <div className="mod-page__inline-error">
          {getApiErrorMessage(error, t("dealGate.override.error"))}
          {readKybWarnings(error).length > 0 ? <KybGateNotice warnings={readKybWarnings(error)} blocked /> : null}
        </div>
      ) : null}
    </div>
  );
};

export default DealKybGateCard;
